<template>
    <div class="mt-3">
        <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mb-3">
            <div>
                <h2 class="text-base md:text-lg font-bold text-gray-800">Timetable Templates</h2>
                <p class="text-gray-500 text-xs">Define bell schedules (periods, breaks) for different sections.</p>
            </div>
            <div v-if="can(currentUser, AppPermissions.TimeTable.Actions.Create, AppPermissions.TimeTable.Resource)"
                class="flex gap-1.5">
                <Button label="Use Wizard" icon="pi pi-sparkles" size="small"
                    class="p-button-primary !bg-primary !border-primary flex-1 md:flex-none" @click="isWizardOpen = true" />
                <Button label="Quick Create" icon="pi pi-plus" size="small"
                    class="p-button-outlined p-button-secondary flex-1 md:flex-none" @click="openCreateDialog" />
            </div>
        </div>

        <!-- Templates List -->
        <div v-if="loading" class="flex justify-center p-6">
            <i class="pi pi-spin pi-spinner text-2xl text-primary"></i>
        </div>
        <div v-else-if="templates.length === 0" class="text-center p-6 bg-white rounded-lg border border-gray-200">
            <p class="text-gray-500 text-sm">No templates found. Create one to get started.</p>
        </div>
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div v-for="template in templates" :key="template.id"
                class="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow flex flex-col">
                <div class="p-3 border-b border-gray-100 flex justify-between items-start">
                    <div>
                        <h3 class="font-semibold text-sm text-gray-800">{{ template.name }}</h3>
                        <span class="text-xs text-gray-500">{{ template.timeSlots.length }} slots defined</span>
                    </div>
                    <div class="flex items-center">
                        <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Update, AppPermissions.TimeTable.Resource)"
                            icon="pi pi-pencil" class="p-button-text p-button-sm text-gray-400 hover:text-primary"
                            @click="editTemplate(template)" />
                        <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Delete, AppPermissions.TimeTable.Resource)"
                            icon="pi pi-trash" class="p-button-text p-button-sm text-gray-400 hover:text-red-600"
                            @click="confirmDeleteTemplate(template)" :loading="deletingTemplateId === template.id"
                            data-testid="btn-timetable-template-delete" />
                    </div>
                </div>
                <div class="p-2.5 flex-1">
                    <ul class="space-y-1">
                        <li v-for="slot in template.timeSlots.slice(0, 3)" :key="slot.id"
                            class="text-xs text-gray-600 flex justify-between">
                            <span>{{ slot.name }}</span>
                            <span class="font-mono text-[10px] bg-gray-50 px-1.5 py-0.5 rounded">{{
                                formatTime(slot.start_time) }}</span>
                        </li>
                        <li v-if="template.timeSlots.length > 3" class="text-[10px] text-gray-400 italic pt-0.5">
                            + {{ template.timeSlots.length - 3 }} more...
                        </li>
                    </ul>
                </div>
                <div class="p-2.5 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-between">
                    <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Create, AppPermissions.TimeTable.Resource)"
                        icon="pi pi-copy" class="p-button-sm p-button-text p-button-secondary"
                        tooltip="Duplicate template" @click="cloneTemplate(template)" :loading="cloningId === template.id" />
                    <Button v-if="can(currentUser, AppPermissions.TimeTable.Actions.Update, AppPermissions.TimeTable.Resource)"
                        label="Manage Slots" class="p-button-sm p-button-outlined p-button-secondary text-xs"
                        @click="manageSlots(template)" />
                </div>
            </div>
        </div>

        <!-- Create/Edit Dialog -->
        <Dialog v-model:visible="isDialogVisible" :header="editingTemplate ? 'Edit Template' : 'Create Template'"
            :modal="true" class="p-fluid w-full max-w-md">
            <div class="field">
                <label for="name" class="font-semibold block mb-1.5 text-sm">Template Name</label>
                <InputText id="name" data-testid="input-timetable-template-name" v-model="form.name" placeholder="e.g. Primary Lower Schedule" class="w-full" />
            </div>
            <template #footer>
                <Button label="Cancel" icon="pi pi-times" class="p-button-text p-button-sm" @click="closeDialog" data-testid="btn-timetable-template-cancel" />
                <Button label="Save" icon="pi pi-check" class="p-button-primary p-button-sm !bg-primary !border-primary"
                    @click="saveTemplate" :loading="saving" data-testid="btn-timetable-template-save" />
            </template>
        </Dialog>

        <!-- Slot Manager Dialog -->
        <Dialog v-model:visible="isSlotManagerVisible" header="Manage Time Slots" :modal="true"
            class="p-fluid w-full max-w-4xl">
            <div v-if="selectedTemplate" class="flex flex-col gap-3">
                <div class="flex justify-between items-center bg-gray-50 p-3 rounded-lg">
                    <div>
                        <span class="text-sm font-bold text-gray-800">{{ selectedTemplate.name }}</span>
                        <p class="text-xs text-gray-500">Configure periods and breaks in chronological order.</p>
                    </div>
                </div>

                <div v-if="!selectedTemplate.timeSlots.length" class="bg-primary/10 border border-primary/20 rounded-lg p-3 mb-2">
                    <h4 class="font-semibold text-primary-700 mb-1.5 flex items-center gap-1 text-sm">
                        <i class="pi pi-bolt text-xs"></i> Quick Generate
                    </h4>
                    <p class="text-xs text-primary-700 mb-2">Auto-fill all periods and breaks at once.</p>
                    <div class="grid grid-cols-2 gap-2 mb-2">
                        <div class="field">
                            <label class="text-xs font-medium mb-1 block">Start Time</label>
                            <Calendar v-model="bulkForm.startTime" timeOnly class="w-full" placeholder="8:00 AM" />
                        </div>
                        <div class="field">
                            <label class="text-xs font-medium mb-1 block">Period (min)</label>
                            <InputText v-model.number="bulkForm.periodDuration" type="number" class="w-full" />
                        </div>
                        <div class="field">
                            <label class="text-xs font-medium mb-1 block">Periods Before Break</label>
                            <InputText v-model.number="bulkForm.periodsBeforeBreak" type="number" class="w-full" />
                        </div>
                        <div class="field">
                            <label class="text-xs font-medium mb-1 block">Break (min)</label>
                            <InputText v-model.number="bulkForm.breakDuration" type="number" class="w-full" />
                        </div>
                        <div class="field">
                            <label class="text-xs font-medium mb-1 block">Periods After Break</label>
                            <InputText v-model.number="bulkForm.periodsAfterBreak" type="number" class="w-full" />
                        </div>
                    </div>
                    <Button label="Generate All Slots" icon="pi pi-bolt" class="p-button-sm w-full"
                        @click="bulkGenerateSlots" :loading="bulkGenerating" />
                </div>

                <div class="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    <!-- Add New Slot Form -->
                    <div class="lg:col-span-1 bg-white p-3 rounded-lg border border-gray-200 h-fit">
                        <h4 class="font-semibold mb-3 text-gray-700 text-sm">{{ editingSlotId ? 'Edit Slot' : 'Add New Slot' }}
                        </h4>
                        <div class="flex flex-col gap-3">
                            <div class="field">
                                <label class="text-xs font-medium mb-1 block">Name</label>
                                <InputText v-model="slotForm.name" placeholder="e.g. Period 1" class="w-full" />
                            </div>
                            <div class="grid grid-cols-2 gap-2">
                                <div class="field">
                                    <label class="text-xs font-medium mb-1 block">Start Time</label>
                                    <Calendar v-model="slotForm.start_time" timeOnly class="w-full"
                                        placeholder="Select Time" />
                                </div>
                                <div class="field">
                                    <label class="text-xs font-medium mb-1 block">End Time</label>
                                    <Calendar v-model="slotForm.end_time" timeOnly class="w-full"
                                        placeholder="Select Time" />
                                </div>
                            </div>
                            <div class="field">
                                <label class="text-xs font-medium mb-1 block">Type</label>
                                <Select v-model="slotForm.type" :options="['lesson', 'break']"
                                    placeholder="Select Type" class="w-full" />
                            </div>
                            <div class="flex gap-1.5">
                                <Button :label="editingSlotId ? 'Update Slot' : 'Add Slot'"
                                    :icon="editingSlotId ? 'pi pi-check' : 'pi pi-plus'"
                                    class="p-button-sm mt-1.5 flex-1 !bg-primary !border-primary" @click="addSlot"
                                    :loading="creatingSlot || deletingSlot" :disabled="!isSlotFormValid" />
                                <Button v-if="editingSlotId" icon="pi pi-times"
                                    class="p-button-sm mt-1.5 p-button-outlined p-button-secondary" @click="cancelEdit"
                                    tooltip="Cancel Edit" />
                            </div>
                        </div>
                    </div>

                    <!-- Slots List -->
                    <div class="lg:col-span-2">
                        <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
                            <table class="w-full text-xs text-left text-gray-500">
                                <thead class="text-[10px] text-gray-700 uppercase bg-gray-50">
                                    <tr>
                                        <th class="px-3 py-2">Order</th>
                                        <th class="px-3 py-2">Time</th>
                                        <th class="px-3 py-2">Name</th>
                                        <th class="px-3 py-2">Type</th>
                                        <th class="px-3 py-2 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(slot, index) in sortedSlots" :key="slot.id"
                                        class="border-b hover:bg-gray-50">
                                        <td class="px-3 py-2 font-medium">{{ index + 1 }}</td>
                                        <td class="px-3 py-2 font-mono">{{ formatTime(slot.start_time) }} - {{
                                            formatTime(slot.end_time) }}</td>
                                        <td class="px-3 py-2 font-medium text-gray-900">{{ slot.name }}</td>
                                        <td class="px-3 py-2">
                                            <span
                                                :class="slot.type === 'break' ? 'bg-yellow-100 text-yellow-800' : 'bg-primary/10 text-primary-700'"
                                                class="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase">
                                                {{ slot.type }}
                                            </span>
                                        </td>
                                        <td class="px-3 py-2 text-right flex justify-end gap-1">
                                            <Button icon="pi pi-pencil"
                                                class="p-button-rounded p-button-secondary p-button-text p-button-sm"
                                                @click="editSlot(slot)" :disabled="!!editingSlotId" />
                                            <Button icon="pi pi-trash"
                                                class="p-button-rounded p-button-danger p-button-text p-button-sm"
                                                @click="deleteSlot(slot.id)" :loading="deletingSlot" />
                                        </td>
                                    </tr>
                                    <tr v-if="selectedTemplate.timeSlots.length === 0">
                                        <td colspan="5" class="px-3 py-6 text-center text-gray-400 italic text-xs">No slots
                                            added yet.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
        <TemplateWizard :visible="isWizardOpen" @close="isWizardOpen = false" @created="onWizardCreated" />
    </div>
</template>

<script setup lang="ts">
import {
  useGetTimetableTemplatesQuery,
  useCreateTimetableTemplateMutation,
  useUpdateTimetableTemplateMutation,
  useDeleteTimetableTemplateMutation,
  useCreateTimetableTimeSlotMutation,
  useDeleteTimetableTimeSlotMutation,
  useUpdateTimetableTimeSlotMutation,
} from '@/types/vue-apollo-helpers';
import type { TimetableTemplate, TimetableTimeSlot } from '@/types/composition-functions';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import TemplateWizard from './TemplateWizard.vue';
import { generateSlotsFromConfig, type TimetableConfig } from '@/constants/timetable-presets';

import { extractGraphQLError } from '@/utils/graphqlError';
const toast = useToast();
const confirm = useConfirm();

const userStore = useUserStore();
const currentUser = computed(() => userStore.currentUser);

// State
const isDialogVisible = ref(false);
const isSlotManagerVisible = ref(false);
const isWizardOpen = ref(false);
const editingTemplate = ref<TimetableTemplate | null>(null);
const selectedTemplate = ref<TimetableTemplate | null>(null);
const form = ref({ name: '' });
const slotForm = ref({ name: '', start_time: null as Date | null, end_time: null as Date | null, type: 'lesson' });
const editingSlotId = ref<string | null>(null);
const editingSlotOrder = ref<number | null>(null);
const cloningId = ref<string | null>(null);
const deletingTemplateId = ref<string | null>(null);
const bulkGenerating = ref(false);
const bulkForm = reactive({
  startTime: (() => { const d = new Date(); d.setHours(8, 0, 0, 0); return d; })(),
  periodDuration: 40,
  periodsBeforeBreak: 3,
  breakDuration: 15,
  periodsAfterBreak: 4,
});

// Queries & Mutations
const { result, loading, refetch } = useGetTimetableTemplatesQuery();
const { mutate: createTemplate, loading: saving } = useCreateTimetableTemplateMutation();
const { mutate: updateTemplate } = useUpdateTimetableTemplateMutation();
const { mutate: deleteTemplateMutation } = useDeleteTimetableTemplateMutation();
const { mutate: createSlot, loading: creatingSlot } = useCreateTimetableTimeSlotMutation();
const { mutate: updateSlot, loading: updatingSlot } = useUpdateTimetableTimeSlotMutation();
const { mutate: deleteSlotMutation, loading: deletingSlot } = useDeleteTimetableTimeSlotMutation();

const templates = computed(() => result.value?.timetableTemplates || []);
const sortedSlots = computed(() => {
  if (!selectedTemplate.value) return [];
  return [...selectedTemplate.value.timeSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
});
const isSlotFormValid = computed(() => slotForm.value.name && slotForm.value.start_time && slotForm.value.end_time);

// Methods
function openCreateDialog() {
  editingTemplate.value = null;
  form.value.name = '';
  isDialogVisible.value = true;
}

function editTemplate(template: TimetableTemplate) {
  editingTemplate.value = template;
  form.value.name = template.name;
  isDialogVisible.value = true;
}

function closeDialog() {
  isDialogVisible.value = false;
}

async function saveTemplate() {
  try {
    if (editingTemplate.value) {
      await updateTemplate({ input: { id: editingTemplate.value.id, name: form.value.name } });
    } else {
      await createTemplate({ input: { name: form.value.name } });
    }
    await refetch();
    closeDialog();
  } catch {
    // Ignore: save failed; the dialog stays open for retry.
  }
}

function manageSlots(template: TimetableTemplate) {
  selectedTemplate.value = JSON.parse(JSON.stringify(template));
  isSlotManagerVisible.value = true;
  resetSlotForm(template);
}

function resetSlotForm(template?: TimetableTemplate) {
  editingSlotId.value = null;
  editingSlotOrder.value = null;

  // Auto-fill next slot if slots exist and we are not editing
  const slots = template?.timeSlots || selectedTemplate.value?.timeSlots || [];
  if (slots.length > 0) {
    predictNextSlot(slots);
  } else {
    const d = new Date();
    d.setHours(8, 0, 0, 0);
    const e = new Date();
    e.setHours(9, 0, 0, 0);
    slotForm.value = { name: 'Period 1', start_time: d, end_time: e, type: 'lesson' };
  }
}

function editSlot(slot: TimetableTimeSlot) {
  editingSlotId.value = slot.id;
  editingSlotOrder.value = slot.order_index || 0; // Capture order if available
  slotForm.value = {
    name: slot.name,
    start_time: parseTimeString(slot.start_time),
    end_time: parseTimeString(slot.end_time),
    type: slot.type,
  };
}

function cancelEdit() {
  resetSlotForm();
}

async function addSlot() {
  if (!isSlotFormValid.value || !selectedTemplate.value) return;

  try {
    const inputData = {
      name: slotForm.value.name,
      start_time: formatTimeForSubmit(slotForm.value.start_time),
      end_time: formatTimeForSubmit(slotForm.value.end_time),
      type: slotForm.value.type,
      order_index: editingSlotOrder.value ?? (selectedTemplate.value.timeSlots.length + 1),
    };

    if (editingSlotId.value) {
      // Update existing slot
      await updateSlot({
        id: editingSlotId.value,
        input: inputData,
      });
    } else {
      // Create new slot
      await createSlot({
        input: {
          ...inputData,
          timetable_template_id: selectedTemplate.value.id,
        },
      });
    }

    // Refresh and update local
    const res = await refetch();
    const updated = res?.data?.timetableTemplates?.find((t) => t.id === selectedTemplate.value!.id);
    if (updated) {
      selectedTemplate.value = updated;
      resetSlotForm(updated); // Reset and predict NEXT if appropriate (or just reset)
    } else {
      resetSlotForm();
    }
  } catch {
    // Ignore: add/update slot failed; the form state is preserved.
  }
}

function deleteSlot(slotId: string) {
  confirm.require({
    message: 'Are you sure you want to delete this slot?',
    header: 'Delete Slot',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async() => {
      try {
        await deleteSlotMutation({ id: slotId });
        const res = await refetch();
        const updated = res?.data?.timetableTemplates?.find((t) => t.id === selectedTemplate.value!.id);
        if (updated) selectedTemplate.value = updated;
        if (editingSlotId.value === slotId) resetSlotForm();
        toast.add({ severity: 'success', summary: 'Slot deleted', life: 2500 });
      } catch (e) {
        toast.add({ severity: 'error', summary: 'Delete failed', detail: extractGraphQLError(e, 'Could not delete the slot.'), life: 4000 });
      }
    },
  });
}

function confirmDeleteTemplate(template: TimetableTemplate) {
  const slotCount = template.timeSlots.length;
  const detail = slotCount > 0
    ? `This template has ${slotCount} time slot${slotCount === 1 ? '' : 's'}. Deleting it will remove the template and its slots, and detach it from any timetables using it.`
    : 'Any timetables using this template will be detached.';
  confirm.require({
    message: `Delete "${template.name}"? ${detail}`,
    header: 'Delete Template',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    rejectClass: 'p-button-secondary p-button-outlined',
    acceptClass: 'p-button-danger',
    accept: async() => {
      deletingTemplateId.value = template.id;
      try {
        await deleteTemplateMutation({ id: template.id });
        await refetch();
        toast.add({ severity: 'success', summary: 'Template deleted', detail: `"${template.name}" was removed.`, life: 3000 });
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Could not delete the template.';
        toast.add({ severity: 'error', summary: 'Delete failed', detail: message, life: 4000 });
      } finally {
        deletingTemplateId.value = null;
      }
    },
  });
}

function predictNextSlot(slots: TimetableTimeSlot[]) {
  // Sort slots to find the last ending one
  const sorted = [...slots].sort((a, b) => a.start_time.localeCompare(b.start_time));
  const last = sorted[sorted.length - 1];
  if (!last) return;

  // Parse times
  const start = parseTimeString(last.start_time || '00:00:00');
  const end = parseTimeString(last.end_time || '00:00:00');
  const durationMs = end.getTime() - start.getTime();

  // New start = Last end
  const newStart = new Date(end);
  // New end = New start + duration
  const newEnd = new Date(newStart.getTime() + durationMs);

  // Predict Name
  let newName = '';
  const nameMatch = last.name.match(/(\d+)$/);
  if (nameMatch) {
    const num = parseInt(nameMatch[0]);
    newName = last.name.replace(/\d+$/, (num + 1).toString());
  } else if (last.type === 'break') {
    // If last was break, return to previous pattern? too complex, just Empty or 'Period'
    newName = 'Period';
  } else {
    newName = last.name; // Copy?
  }

  slotForm.value = {
    name: newName,
    start_time: newStart,
    end_time: newEnd,
    type: 'lesson',
  };
}

function parseTimeString(t: string) {
  const d = new Date();
  const [h, m] = (t || '00:00').split(':');
  d.setHours(parseInt(h || '0'), parseInt(m || '0'), 0, 0);
  return d;
}

function formatTime(time: string) {
  if (!time) return '';
  const [hours, minutes] = time.split(':');
  return `${hours}:${minutes}`;
}

function formatTimeForSubmit(date: Date | null) {
  if (!date) return '';
  const h = date.getHours().toString().padStart(2, '0');
  const m = date.getMinutes().toString().padStart(2, '0');
  return `${h}:${m}:00`;
}

async function cloneTemplate(template: TimetableTemplate) {
  cloningId.value = template.id;
  try {
    const result = await createTemplate({ input: { name: `${template.name} (Copy)` } });
    const newId = result?.data?.createTimetableTemplate?.id;
    if (!newId) return;

    const sorted = [...template.timeSlots].sort((a, b) => a.start_time.localeCompare(b.start_time));
    for (const slot of sorted) {
      await createSlot({
        input: {
          timetable_template_id: newId,
          name: slot.name,
          start_time: slot.start_time,
          end_time: slot.end_time,
          type: slot.type,
          order_index: slot.order_index || 0,
        },
      });
    }
    await refetch();
  } catch {
    // Ignore: clone failed; the finally block resets cloningId.
  } finally {
    cloningId.value = null;
  }
}

async function bulkGenerateSlots() {
  if (!selectedTemplate.value) return;
  bulkGenerating.value = true;
  try {
    const h = bulkForm.startTime.getHours().toString().padStart(2, '0');
    const m = bulkForm.startTime.getMinutes().toString().padStart(2, '0');
    const config: TimetableConfig = {
      startTime: `${h}:${m}`,
      periodDuration: bulkForm.periodDuration,
      periodsBeforeBreak: bulkForm.periodsBeforeBreak,
      breakDuration: bulkForm.breakDuration,
      periodsAfterBreak: bulkForm.periodsAfterBreak,
    };
    const slots = generateSlotsFromConfig(config);

    for (const slot of slots) {
      await createSlot({
        input: {
          timetable_template_id: selectedTemplate.value.id,
          name: slot.name,
          start_time: slot.start_time,
          end_time: slot.end_time,
          type: slot.type,
          order_index: slot.order_index,
        },
      });
    }

    const res = await refetch();
    const updated = res?.data?.timetableTemplates?.find((t) => t.id === selectedTemplate.value!.id);
    if (updated) {
      selectedTemplate.value = JSON.parse(JSON.stringify(updated));
      resetSlotForm(updated);
    }
  } catch {
    // Ignore: bulk generate failed; the finally block resets the flag.
  } finally {
    bulkGenerating.value = false;
  }
}

async function onWizardCreated() {
  await refetch();
}
</script>
